import { Container, Graphics } from "pixi.js";
import { DEFAULT_PITCH_SKIN, FIELD, pitchSkin, type PitchSkinId } from "@golazo/shared";

/** Polvo de cancha muy sutil: da vida al campo sin distraer. */
export interface AmbientLayer {
  container: Container;
  update(dt: number): void;
}

interface Mote {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  alpha: number;
  phase: number;
}

const COUNT = 18;

export function createAmbientLayer(skinId: PitchSkinId = DEFAULT_PITCH_SKIN): AmbientLayer {
  const env = pitchSkin(skinId).env;
  const container = new Container();
  container.label = "ambient";
  const gfx = new Graphics();
  container.addChild(gfx);

  // Las brasas suben y brillan; la nieve cae despacio; el polvo solo flota.
  const rising = env.moteDrift === "rise";
  const falling = env.moteDrift === "fall";
  const count = rising || falling ? COUNT * 2 : COUNT;

  const motes: Mote[] = Array.from({ length: count }, () => spawnMote());

  function spawnMote(): Mote {
    return {
      x: Math.random() * FIELD.WIDTH,
      y: Math.random() * FIELD.HEIGHT,
      vx: (Math.random() - 0.5) * (falling ? 14 : 8),
      vy: rising ? -(14 + Math.random() * 22) : falling ? 16 + Math.random() * 20 : (Math.random() - 0.5) * 6,
      size: 1.2 + Math.random() * (rising ? 1.6 : 2.2),
      alpha: (0.04 + Math.random() * 0.06) * (rising || falling ? 2.2 : 1),
      phase: Math.random() * Math.PI * 2,
    };
  }

  return {
    container,

    update(dt: number): void {
      gfx.clear();
      for (const m of motes) {
        m.x += m.vx * dt;
        m.y += m.vy * dt;
        m.phase += dt * (rising ? 3.2 : 1.4);
        if (falling) m.x += Math.sin(m.phase) * 10 * dt;

        if (m.x < 0) m.x = FIELD.WIDTH;
        if (m.x > FIELD.WIDTH) m.x = 0;
        if (m.y < 0) m.y = FIELD.HEIGHT;
        if (m.y > FIELD.HEIGHT) m.y = 0;

        const twinkle = 0.65 + Math.sin(m.phase) * 0.35;
        gfx.circle(m.x, m.y, m.size).fill({ color: env.mote, alpha: m.alpha * twinkle });
      }
    },
  };
}
